// The locked control (DESIGN.web.md 7.6): the same control at the secondary
// ink, holding its state, keyboard-focusable and inert, with a caption that
// names who can change it. Not hidden, not disabled-gray, no padlock. The look
// is the `[data-locked]` rules in app.css; these are the one way a page sets it.

import { Box, Menu, Switch, Text, VisuallyHidden, type SwitchProps } from '@mantine/core';
import type { Icon } from '@tabler/icons-react';
import { Fragment, useId, type ReactNode } from 'react';

import { ICON_INLINE } from '../theme';
import { ActionsTrigger, MenuAction } from './actions';

export const LOCKED = { locked: true } as const;

/**
 * Props that lock a Button or an ActionIcon: it keeps its place and its focus,
 * does nothing, and is described by the caption beside it. The caller leaves
 * out `onClick`.
 */
export function locked(captionId?: string) {
  return { mod: LOCKED, 'aria-disabled': true, ...(captionId ? { 'aria-describedby': captionId } : {}) } as const;
}

/** A Menu.Item that stays in the list and in the arrow-key order, and does nothing. */
export function lockedItem(captionId: string) {
  return { mod: LOCKED, 'aria-disabled': true, closeMenuOnClick: false, 'aria-describedby': captionId } as const;
}

/**
 * A text input, number input, or select that shows its value and takes focus
 * but not a change. Its description carries the caption (see `lockedNote`),
 * since the input's own description is what a screen reader reads with it.
 */
export const LOCKED_INPUT = { readOnly: true, mod: LOCKED } as const;

/** A locked input's parts in reading order: the value first, then what it does and who can change it. */
export const LOCKED_ORDER: ('label' | 'input' | 'description' | 'error')[] = ['label', 'input', 'description', 'error'];

/**
 * Why a menu's locked lines are locked, said to a screen reader only. In a
 * menu the dimmed line already says it is not yours to take, and a menu
 * carries no explanations (DESIGN.web.md 7.6); the locked items point here
 * with aria-describedby, so who can do it is still read with each.
 */
export function MenuCaption({ id, children }: { id: string; children: ReactNode }) {
  return <VisuallyHidden id={id}>{children}</VisuallyHidden>;
}

/**
 * An actions menu for someone who cannot use it: the same button, opening the
 * same items, each locked and dimmed, described by one caption a screen
 * reader hears. Groups are separated by a divider, as in the live menu.
 * `compact` is the table-row size.
 */
export function LockedActions({ caption, groups, label, compact = false }: { caption: ReactNode; groups: string[][]; label?: string; compact?: boolean }) {
  const captionId = useId();
  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <ActionsTrigger label={label ?? 'Actions'} {...(compact ? { size: 28 } : {})} />
      </Menu.Target>
      <Menu.Dropdown>
        <MenuCaption id={captionId}>{caption}</MenuCaption>
        {groups.map((items, i) => (
          <Fragment key={items.join()}>
            {i > 0 && <Menu.Divider />}
            {items.map((item) => <Menu.Item key={item} {...lockedItem(captionId)}>{item}</Menu.Item>)}
          </Fragment>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

/** A line of a record's menu, and whether the person signed in may take it. */
export interface MenuLine {
  label: string;
  icon: Icon;
  /** Taken when chosen; left out, the line is locked under `caption`. */
  onClick?: (() => void) | undefined;
  danger?: boolean;
  caption?: ReactNode;
  /** A mark at the line's end, such as one for a page that opens elsewhere. */
  rightSection?: ReactNode;
}

/**
 * One group of a record's menu where some lines may be the person's and
 * some not, such as a staff role that may suspend a device but not revoke
 * it. What they may take is live; what they may not stays in its place,
 * locked and dimmed, and lines locked for the same reason share one caption
 * that a screen reader hears with each (MenuCaption).
 */
export function MenuLines({ lines }: { lines: MenuLine[] }) {
  const runs: { live: boolean; caption: ReactNode; lines: MenuLine[] }[] = [];
  for (const line of lines) {
    const live = !!line.onClick;
    const last = runs.at(-1);
    if (last && last.live === live && (live || last.caption === line.caption)) last.lines.push(line);
    else runs.push({ live, caption: line.caption, lines: [line] });
  }
  return <>{runs.map((run) => (run.live
    ? run.lines.map((l) => <MenuAction key={l.label} icon={l.icon} danger={!!l.danger} onClick={l.onClick} rightSection={l.rightSection}>{l.label}</MenuAction>)
    : <LockedRun key={run.lines.map((l) => l.label).join()} lines={run.lines} caption={run.caption} />))}</>;
}

function LockedRun({ lines, caption }: { lines: MenuLine[]; caption: ReactNode }) {
  const captionId = useId();
  return (
    <>
      {lines.map(({ label, icon: Glyph }) => (
        <Menu.Item key={label} leftSection={<Glyph {...ICON_INLINE} color="var(--mantine-color-dimmed)" />} {...lockedItem(captionId)}>{label}</Menu.Item>
      ))}
      <MenuCaption id={captionId}>{caption}</MenuCaption>
    </>
  );
}

/** The caption under a locked control or a group of them. */
export function LockedCaption({ id, children }: { id?: string | undefined; children: ReactNode }) {
  return <Text id={id} size="xs" c="dimmed">{children}</Text>;
}

/** A locked setting's description: what it does, then, on its own line, who can change it. */
export function lockedNote(hint: ReactNode, caption: ReactNode): ReactNode {
  return <>{hint}<Box component="span" display="block" mt={4}>{caption}</Box></>;
}

/** Props that lock a Switch where it stands: it holds its state and ignores a change. */
export function lockedSwitch(captionId?: string) {
  return { onChange: () => undefined, ...locked(captionId) };
}

/** The same switch, dimmed to the secondary ink, holding its state. */
export function LockedSwitch(props: Omit<SwitchProps, 'onChange' | 'mod'>) {
  return <Switch {...props} {...lockedSwitch()} />;
}
